if (!customElements.get('article-product-grid-cta')) {
  customElements.define(
    'article-product-grid-cta',
    class ArticleProductGridCTA extends HTMLElement {
      constructor() {
        super();

        this.sizeFilter = null;
        this.clearButton = null;
        this.message = null;
        this.cards = [];
        this.selectedSize = '';
      }

      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.dataset.initialized = 'true';
        this.initialize();
      }

      async initialize() {
        this.sizeFilter = this.querySelector(
          '.js-article-cta-size-filter'
        );

        this.clearButton = this.querySelector(
          '.js-article-cta-clear'
        );

        this.message = this.querySelector(
          '.js-article-cta-message'
        );

        const elements = Array.from(
          this.querySelectorAll('.js-article-cta-card')
        );

        this.cards = elements.map((element) => ({
          element,
          handle: element.dataset.productHandle,
          currentColor: element.dataset.currentColor || '',
          product: null,
          sizeIndex: -1,
          colorIndex: -1,
          selectedColor: '',
          selectedVariant: null,

          image: element.querySelector(
            '.js-article-cta-image'
          ),

          price: element.querySelector(
            '.js-article-cta-price'
          ),

          comparePrice: element.querySelector(
            '.js-article-cta-compare-price'
          ),

          swatches: element.querySelector(
            '.js-article-cta-swatches'
          ),

          addButton: element.querySelector(
            '.js-article-cta-add'
          ),

          cardMessage: element.querySelector(
            '.js-article-cta-card-message'
          ),
        }));

        await Promise.all(
          this.cards.map((card) =>
            this.loadProduct(card)
          )
        );

        this.cards = this.cards.filter(
          (card) => card.product
        );

        this.cards.forEach((card) => {
          this.configureOptions(card);
          this.buildColorSwatches(card);
          this.bindCardEvents(card);
        });

        this.buildSizeFilter();
        this.bindEvents();
        this.updateCards();
      }

      async loadProduct(card) {
        if (!card.handle) return;

        const root =
          window.Shopify?.routes?.root || '/';

        try {
          const response = await fetch(
            `${root}products/${encodeURIComponent(
              card.handle
            )}.js`,
            {
              headers: {
                Accept: 'application/json',
              },
            }
          );

          if (!response.ok) {
            throw new Error(
              `Unable to load ${card.handle}`
            );
          }

          const product = await response.json();

          if (
            !Array.isArray(product.variants) ||
            !product.variants.length
          ) {
            throw new Error(
              `No variants found for ${card.handle}`
            );
          }

          card.product = product;
        } catch (error) {
          console.error(
            'Product grid CTA:',
            error
          );

          card.element.classList.add(
            'is-size-unavailable'
          );

          if (card.cardMessage) {
            card.cardMessage.textContent =
              'Product options could not be loaded.';

            card.cardMessage.classList.add(
              'is-error'
            );
          }
        }
      }

      configureOptions(card) {
        const optionNames = Array.isArray(
          card.product.options
        )
          ? card.product.options.map((option) => {
              if (typeof option === 'string') {
                return option;
              }

              return option.name || '';
            })
          : [];

        card.sizeIndex = optionNames.findIndex(
          (name) =>
            /^(size|bra size)$/i.test(
              String(name).trim()
            )
        );

        if (card.sizeIndex < 0) {
          card.sizeIndex = optionNames.findIndex(
            (name) =>
              /size/i.test(String(name))
          );
        }

        /*
         * ThirdLove currently uses option2 for size in its
         * product mini-card code. Use option2 as a fallback.
         */
        if (card.sizeIndex < 0) {
          card.sizeIndex =
            optionNames.length > 1 ? 1 : 0;
        }

        card.colorIndex = optionNames.findIndex(
          (name) =>
            /(color|colour|shade)/i.test(
              String(name)
            )
        );

        const firstVariant =
          card.product.variants.find(
            (variant) =>
              variant.available !== false
          ) || card.product.variants[0];

        if (
          card.colorIndex >= 0 &&
          firstVariant
        ) {
          card.selectedColor = String(
            this.getVariantOption(
              firstVariant,
              card.colorIndex
            )
          ).trim();
        } else {
          card.selectedColor =
            card.currentColor;
        }
      }

      bindEvents() {
        if (this.sizeFilter) {
          this.sizeFilter.addEventListener(
            'change',
            () => {
              this.selectedSize =
                this.sizeFilter.value;

              this.updateCards();
            }
          );
        }

        if (this.clearButton) {
          this.clearButton.addEventListener(
            'click',
            () => {
              this.selectedSize = '';

              if (this.sizeFilter) {
                this.sizeFilter.value = '';
              }

              this.updateCards();
            }
          );
        }
      }

      bindCardEvents(card) {
        if (!card.addButton) return;

        card.addButton.addEventListener(
          'click',
          () => {
            this.addToCart(card);
          }
        );
      }

      buildSizeFilter() {
        if (!this.sizeFilter) return;

        const sizes = new Set();

        this.cards.forEach((card) => {
          card.product.variants.forEach(
            (variant) => {
              const size = String(
                this.getVariantOption(
                  variant,
                  card.sizeIndex
                )
              ).trim();

              if (size) {
                sizes.add(size);
              }
            }
          );
        });

        const sortedSizes = Array.from(
          sizes
        ).sort((a, b) =>
          a.localeCompare(b, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        );

        sortedSizes.forEach((size) => {
          const option =
            document.createElement('option');

          option.value = size;
          option.textContent = size;

          this.sizeFilter.appendChild(option);
        });
      }

      buildColorSwatches(card) {
        if (!card.swatches) return;

        const colors = new Set();

        if (card.colorIndex >= 0) {
          card.product.variants.forEach(
            (variant) => {
              const color = String(
                this.getVariantOption(
                  variant,
                  card.colorIndex
                )
              ).trim();

              if (color) {
                colors.add(color);
              }
            }
          );
        }

        /*
         * ThirdLove may store colors as separate products.
         * When no Color variant option exists, show the
         * current color derived from the product handle.
         */
        if (
          !colors.size &&
          card.currentColor
        ) {
          colors.add(card.currentColor);
        }

        Array.from(colors).forEach(
          (color, index) => {
            const swatch =
              document.createElement('button');

            const selected =
              color === card.selectedColor ||
              (!card.selectedColor &&
                index === 0);

            if (
              !card.selectedColor &&
              index === 0
            ) {
              card.selectedColor = color;
            }

            swatch.type = 'button';

            swatch.className =
              'article-product-grid-cta__swatch';

            swatch.dataset.color = color;
            swatch.title = color;

            swatch.setAttribute(
              'aria-label',
              `Select ${color}`
            );

            swatch.setAttribute(
              'aria-pressed',
              selected ? 'true' : 'false'
            );

            swatch.classList.toggle(
              'is-selected',
              selected
            );

            swatch.style.setProperty(
              '--cta-swatch-color',
              this.getSwatchColor(color)
            );

            swatch.addEventListener(
              'click',
              () => {
                card.selectedColor = color;

                card.swatches
                  .querySelectorAll(
                    '.article-product-grid-cta__swatch'
                  )
                  .forEach((button) => {
                    const isSelected =
                      button.dataset.color ===
                      color;

                    button.classList.toggle(
                      'is-selected',
                      isSelected
                    );

                    button.setAttribute(
                      'aria-pressed',
                      isSelected
                        ? 'true'
                        : 'false'
                    );
                  });

                this.updateCard(card);
              }
            );

            card.swatches.appendChild(
              swatch
            );
          }
        );
      }

      updateCards() {
        let matchingProducts = 0;

        this.cards.forEach((card) => {
          if (this.updateCard(card)) {
            matchingProducts += 1;
          }
        });

        if (this.clearButton) {
          this.clearButton.hidden =
            !this.selectedSize;
        }

        if (!this.message) return;

        if (!this.cards.length) {
          this.message.textContent =
            'Product options could not be loaded.';
        } else if (!this.selectedSize) {
          this.message.textContent =
            'Choose a size to enable Add to Cart.';
        } else if (!matchingProducts) {
          this.message.textContent =
            `No products are currently available in ${this.selectedSize}.`;
        } else {
          this.message.textContent =
            `${matchingProducts} product${
              matchingProducts === 1
                ? ''
                : 's'
            } available in ${this.selectedSize}.`;
        }
      }

      updateCard(card) {
        const variant =
          this.findMatchingVariant(card);

        card.selectedVariant = variant;

        const isAvailable = Boolean(
          this.selectedSize &&
          variant &&
          variant.available !== false
        );

        card.element.classList.toggle(
          'is-size-unavailable',
          Boolean(this.selectedSize) &&
            !isAvailable
        );

        if (card.addButton) {
          card.addButton.disabled =
            !isAvailable;

          card.addButton.textContent =
            'Add to Cart';
        }

        if (card.cardMessage) {
          card.cardMessage.classList.remove(
            'is-error'
          );

          if (!this.selectedSize) {
            card.cardMessage.textContent =
              'Select a size above.';
          } else if (!isAvailable) {
            card.cardMessage.textContent =
              `Unavailable in ${this.selectedSize}.`;
          } else {
            card.cardMessage.textContent = '';
          }
        }

        if (variant) {
          this.updateCardPrice(
            card,
            variant
          );

          this.updateCardImage(
            card,
            variant
          );
        }

        return isAvailable;
      }

      findMatchingVariant(card) {
        let variants = [
          ...card.product.variants,
        ];

        if (this.selectedSize) {
          variants = variants.filter(
            (variant) =>
              String(
                this.getVariantOption(
                  variant,
                  card.sizeIndex
                )
              ).trim() ===
              this.selectedSize
          );
        }

        if (
          card.colorIndex >= 0 &&
          card.selectedColor
        ) {
          const colorMatches =
            variants.filter(
              (variant) =>
                String(
                  this.getVariantOption(
                    variant,
                    card.colorIndex
                  )
                ).trim() ===
                card.selectedColor
            );

          if (colorMatches.length) {
            variants = colorMatches;
          }
        }

        return (
          variants.find(
            (variant) =>
              variant.available !== false
          ) ||
          variants[0] ||
          null
        );
      }

      getVariantOption(variant, index) {
        if (!variant || index < 0) {
          return '';
        }

        if (Array.isArray(variant.options)) {
          return variant.options[index] || '';
        }

        return (
          variant[`option${index + 1}`] ||
          ''
        );
      }

      updateCardPrice(card, variant) {
        if (card.price) {
          card.price.textContent =
            this.formatMoney(variant.price);
        }

        if (!card.comparePrice) return;

        if (
          variant.compare_at_price &&
          variant.compare_at_price >
            variant.price
        ) {
          card.comparePrice.textContent =
            this.formatMoney(
              variant.compare_at_price
            );

          card.comparePrice.hidden = false;
        } else {
          card.comparePrice.textContent = '';
          card.comparePrice.hidden = true;
        }
      }

      updateCardImage(card, variant) {
        if (
          !card.image ||
          !variant.featured_image
        ) {
          return;
        }

        const image =
          typeof variant.featured_image ===
          'string'
            ? variant.featured_image
            : variant.featured_image.src ||
              variant.featured_image.url;

        if (!image) return;

        card.image.src = this.resizeImage(
          image,
          700
        );

        card.image.srcset = '';
      }

      resizeImage(url, width) {
        try {
          const imageUrl = new URL(
            url,
            window.location.origin
          );

          imageUrl.searchParams.set(
            'width',
            width
          );

          return imageUrl.toString();
        } catch (_error) {
          return url;
        }
      }

      formatMoney(cents) {
        const currency =
          window.Shopify?.currency?.active ||
          'USD';

        try {
          return new Intl.NumberFormat(
            undefined,
            {
              style: 'currency',
              currency,
            }
          ).format(
            Number(cents || 0) / 100
          );
        } catch (_error) {
          return `$${(
            Number(cents || 0) / 100
          ).toFixed(2)}`;
        }
      }

      getSwatchColor(colorName) {
        const normalized =
          String(colorName)
            .toLowerCase()
            .trim();

        const colors = {
          black: '#111111',
          white: '#ffffff',
          ivory: '#f4efe5',
          cream: '#eee6d6',
          nude: '#d4b29b',
          beige: '#d6bca9',
          taupe: '#b69b87',
          mocha: '#8b6250',
          brown: '#69483b',
          chocolate: '#563b32',
          pink: '#e8b8b8',
          blush: '#e7c2bc',
          rose: '#c98f94',
          red: '#a92f35',
          burgundy: '#6e2635',
          purple: '#87708d',
          lavender: '#b5a2c6',
          blue: '#7893ac',
          navy: '#24344d',
          green: '#778778',
          gray: '#a7a7a7',
          grey: '#a7a7a7',
          silver: '#b8b8b8',
          gold: '#b89658',
        };

        if (colors[normalized]) {
          return colors[normalized];
        }

        const partialMatch =
          Object.keys(colors).find((key) =>
            normalized.includes(key)
          );

        return partialMatch
          ? colors[partialMatch]
          : '#d7d2ce';
      }

      async addToCart(card) {
        const variant =
          card.selectedVariant;

        if (
          !variant ||
          variant.available === false
        ) {
          return;
        }

        const button = card.addButton;
        const originalText =
          button.textContent;

        button.disabled = true;

        button.classList.add(
          'is-loading'
        );

        button.textContent = 'Adding...';

        const cartElement =
          document.querySelector(
            'cart-drawer'
          ) ||
          document.querySelector(
            'cart-notification'
          ) ||
          document.querySelector(
            'component-cart'
          );

        const payload = {
          items: [
            {
              id: Number(variant.id),
              quantity: 1,
            },
          ],
        };

        if (
          cartElement &&
          typeof cartElement.getSectionsToRender ===
            'function'
        ) {
          payload.sections = cartElement
            .getSectionsToRender()
            .map((section) => section.id);

          payload.sections_url =
            window.location.pathname;
        }

        const root =
          window.Shopify?.routes?.root || '/';

        try {
          const response = await fetch(
            `${root}cart/add.js`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',
              },

              body: JSON.stringify(payload),
            }
          );

          const result =
            await response.json();

          if (
            !response.ok ||
            result.status
          ) {
            throw new Error(
              result.description ||
                result.message ||
                'This product could not be added.'
            );
          }

          button.textContent = 'Added';

          if (card.cardMessage) {
            card.cardMessage.textContent =
              'Added to your cart.';
          }

          if (
            cartElement &&
            typeof cartElement.renderContents ===
              'function' &&
            result.sections
          ) {
            cartElement.renderContents(
              result
            );
          }

          if (
            cartElement &&
            typeof cartElement.open ===
              'function'
          ) {
            cartElement.open();
          }

          document.dispatchEvent(
            new CustomEvent(
              'cart:refresh',
              {
                bubbles: true,
                detail: result,
              }
            )
          );

          document.dispatchEvent(
            new CustomEvent(
              'cart:updated',
              {
                bubbles: true,
                detail: result,
              }
            )
          );

          window.dispatchEvent(
            new CustomEvent(
              'cart-drawer:refresh',
              {
                detail: result,
              }
            )
          );

          setTimeout(() => {
            button.textContent =
              originalText;

            button.disabled = false;
          }, 1500);
        } catch (error) {
          console.error(
            'CTA Add to Cart error:',
            error
          );

          button.textContent =
            originalText;

          button.disabled = false;

          if (card.cardMessage) {
            card.cardMessage.textContent =
              error.message ||
              'This product could not be added.';

            card.cardMessage.classList.add(
              'is-error'
            );
          }
        } finally {
          button.classList.remove(
            'is-loading'
          );
        }
      }
    }
  );
}